'use client';

import { c as _c } from "react/compiler-runtime";
import { ArrowUpRight, Github, Check, ExternalLink, FileText, ArrowRight } from 'lucide-react';
import Link from 'next/link';
import { Mode, ModeConfig } from '@/components/shared/types';
import { getBorderBgColor } from '@/components/shared/helpers';
import { useTranslation } from '@/context/I18nContext';
import { getProjectsData } from '@/data/loader';
import { trackEvent } from '@/lib/umami';
interface Project {
  id: string;
  title: string;
  year: string;
  links?: {
    case?: string;
    repo?: string;
    demo?: string;
    pdf?: string;
  };
  tech: string[];
  descriptions?: Partial<Record<Mode, string>>;
  desc?: string;
}
interface ProjectsSectionProps {
  activeMode: Mode;
  design: ModeConfig['design'];
  isDark: boolean;
}
export default function ProjectsSection(t0) {
  const $ = _c(65);
  const {
    activeMode,
    design,
    isDark
  } = t0;
  const {
    locale,
    t
  } = useTranslation();
  let t1;
  if ($[0] !== activeMode || $[1] !== locale) {
    t1 = (project, linkType) => {
      trackEvent("project_link_click", {
        project_id: project.id,
        project_title: project.title,
        link_type: linkType,
        mode: activeMode,
        locale
      });
    };
    $[0] = activeMode;
    $[1] = locale;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleProjectLinkClick = t1;
  let T0;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[3] !== activeMode || $[4] !== design.accent || $[5] !== design.border || $[6] !== design.borderRadius || $[7] !== design.cardBg || $[8] !== design.cardBorder || $[9] !== design.fontFamily || $[10] !== design.shadow || $[11] !== design.textPrimary || $[12] !== design.textSecondary || $[13] !== handleProjectLinkClick || $[14] !== isDark || $[15] !== locale || $[16] !== t) {
    const projData = getProjectsData(locale);
    const filteredProjects = (projData as Project[]).slice(0, 3);
    t6 = "projects-section";
    const t9 = `text-sm font-bold uppercase tracking-widest flex items-center gap-4 w-full ${design.textSecondary}`;
    let t10;
    if ($[25] !== activeMode) {
      t10 = activeMode === "backend" && <span className="terminal-prompt" />;
      $[25] = activeMode;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    let t11;
    if ($[27] !== t) {
      t11 = t("common.selectedProjects");
      $[27] = t;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    let t12;
    if ($[29] !== activeMode) {
      t12 = getBorderBgColor(activeMode);
      $[29] = activeMode;
      $[30] = t12;
    } else {
      t12 = $[30];
    }
    const t13 = `h-px flex-1 ${t12} opacity-30`;
    let t14;
    if ($[31] !== t13) {
      t14 = <span className={t13} />;
      $[31] = t13;
      $[32] = t14;
    } else {
      t14 = $[32];
    }
    if ($[33] !== t10 || $[34] !== t11 || $[35] !== t14 || $[36] !== t9) {
      t7 = <div className="mb-10 flex items-center justify-between"><h2 className={t9}>{t10}{t11}{t14}</h2></div>;
      $[33] = t10;
      $[34] = t11;
      $[35] = t14;
      $[36] = t9;
      $[37] = t7;
    } else {
      t7 = $[37];
    }
    let t15;
    if ($[38] !== activeMode || $[39] !== design.border || $[40] !== design.borderRadius || $[41] !== design.cardBg || $[42] !== design.cardBorder || $[43] !== design.fontFamily || $[44] !== design.shadow || $[45] !== design.textPrimary || $[46] !== design.textSecondary || $[47] !== handleProjectLinkClick || $[48] !== isDark || $[49] !== t) {
      t15 = project_0 => <article key={project_0.id} id={`proj-${project_0.id}`} data-sublayer-id={`proj-${project_0.id}`} data-sublayer-name={project_0.title} className={`group relative ${design.borderRadius === "rounded-xl" ? "rounded-xl" : design.borderRadius === "rounded-2xl" ? "rounded-2xl" : design.borderRadius === "rounded-3xl" ? "rounded-3xl" : "rounded-lg"} ${activeMode === "ai" ? "p-10" : "p-8"} border transition-all duration-300 z-10 ${activeMode === "mobile" ? "glass-effect glass-effect-hover border-slate-400/20 hover:border-sky-400/50 hover:shadow-xl" : activeMode === "blockchain" ? `${design.cardBg} ${design.cardBorder} hover:border-[#00FF41] hover:shadow-[0_0_30px_rgba(0,255,65,0.5)]` : activeMode === "frontend" ? `${design.cardBg} ${design.cardBorder} hover:border-rose-300 hover:shadow-xl` : activeMode === "qa" ? `${design.cardBg} ${design.cardBorder} hover:border-cyan-300 hover:shadow-lg` : activeMode === "ai" ? `${design.cardBg} ${design.cardBorder} hover:border-[#00F0FF] hover:shadow-lg hover:shadow-[#00F0FF]/30` : isDark ? `${design.cardBg} ${design.cardBorder} hover:border-white/20 hover:bg-white/10 ${design.shadow}` : `${design.cardBg} ${design.cardBorder} hover:border-slate-300 hover:shadow-lg`}`}><div className="mb-6 flex flex-row items-center justify-between"><div className="flex items-center gap-3"><h3 className={`text-2xl font-bold ${design.textPrimary} ${activeMode === "blockchain" ? "green-glow" : ""}`}>{activeMode === "qa" && <Check className="inline h-5 w-5 mr-2 text-green-500" />}{project_0.title}</h3><span className={`text-xs ${design.fontFamily === "mono" ? "font-mono" : ""} ${design.textSecondary} opacity-70`}>{activeMode === "backend" && <span className="terminal-prompt" />}{project_0.year}</span></div><div className="flex gap-3 justify-end">{project_0.links?.case && <a href={project_0.links.case} target="_blank" rel="noopener noreferrer" className={`p-2.5 rounded-full transition-colors ${activeMode === "mobile" ? "glass-effect hover:bg-white/20 text-white" : isDark ? "bg-white/5 hover:bg-white/20 text-white" : activeMode === "frontend" ? "bg-rose-100 hover:bg-rose-200 text-rose-700" : activeMode === "qa" ? "bg-white/40 backdrop-blur border border-cyan-200/60 text-cyan-800 shadow-sm shadow-cyan-100/50 hover:bg-white/60" : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`} aria-label={t("accessibility.openCaseStudy", {
              title: project_0.title
            })} onClick={() => handleProjectLinkClick(project_0, "case")}><ArrowUpRight className="h-5 w-5" aria-hidden="true" /></a>}{project_0.links?.repo && <a href={project_0.links.repo} target="_blank" rel="noopener noreferrer" className={`p-2.5 rounded-full transition-colors ${activeMode === "mobile" ? "glass-effect hover:bg-white/20 text-white" : isDark ? "bg-white/5 hover:bg-white/20 text-white" : activeMode === "frontend" ? "bg-rose-100 hover:bg-rose-200 text-rose-700" : activeMode === "qa" ? "bg-white/40 backdrop-blur border border-cyan-200/60 text-cyan-800 shadow-sm shadow-cyan-100/50 hover:bg-white/60" : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`} aria-label={t("accessibility.openRepository", {
              title: project_0.title
            })} onClick={() => handleProjectLinkClick(project_0, "repo")}><Github className="h-5 w-5" aria-hidden="true" /></a>}{project_0.links?.demo && <a href={project_0.links.demo} target="_blank" rel="noopener noreferrer" className={`p-2.5 rounded-full transition-colors ${activeMode === "mobile" ? "glass-effect hover:bg-white/20 text-white" : isDark ? "bg-white/5 hover:bg-white/20 text-white" : activeMode === "frontend" ? "bg-rose-100 hover:bg-rose-200 text-rose-700" : activeMode === "qa" ? "bg-white/40 backdrop-blur border border-cyan-200/60 text-cyan-800 shadow-sm shadow-cyan-100/50 hover:bg-white/60" : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`} aria-label={t("accessibility.openDemo", {
              title: project_0.title
            })} onClick={() => handleProjectLinkClick(project_0, "demo")}><ExternalLink className="h-5 w-5" aria-hidden="true" /></a>}{project_0.links?.pdf && <a href={project_0.links.pdf} target="_blank" rel="noopener noreferrer" className={`p-2.5 rounded-full transition-colors ${activeMode === "mobile" ? "glass-effect hover:bg-white/20 text-white" : isDark ? "bg-white/5 hover:bg-white/20 text-white" : activeMode === "frontend" ? "bg-rose-100 hover:bg-rose-200 text-rose-700" : activeMode === "qa" ? "bg-white/40 backdrop-blur border border-cyan-200/60 text-cyan-800 shadow-sm shadow-cyan-100/50 hover:bg-white/60" : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`} aria-label={t("accessibility.openPdf", {
              title: project_0.title
            })} onClick={() => handleProjectLinkClick(project_0, "pdf")}><FileText className="h-5 w-5" aria-hidden="true" /></a>}</div></div><p className={`text-base leading-7 mb-8 ${design.textSecondary}`}>{project_0.desc || project_0.descriptions?.pm || Object.values(project_0.descriptions || {})[0] || ""}</p><div className={`flex flex-wrap gap-2 border-t pt-6 ${design.border} opacity-30`}>{project_0.tech.map(t_0 => <span key={t_0} className={`text-xs font-medium px-2 py-1 ${design.borderRadius} ${activeMode === "blockchain" ? "text-[#00FF41] bg-[#003300] green-glow" : activeMode === "backend" ? "text-emerald-400 bg-emerald-400/10" : activeMode === "mobile" ? "text-sky-300 glass-effect border border-sky-400/30" : activeMode === "frontend" ? "text-rose-700 bg-rose-100" : activeMode === "qa" ? "text-cyan-800 bg-white/40 backdrop-blur border border-cyan-200/60 shadow-sm shadow-cyan-100/50" : "text-indigo-700 bg-indigo-50"}`}>{activeMode === "qa" && <Check className="inline h-3 w-3 mr-1 text-green-500" />}#{t_0}</span>)}</div></article>;
      $[38] = activeMode;
      $[39] = design.border;
      $[40] = design.borderRadius;
      $[41] = design.cardBg;
      $[42] = design.cardBorder;
      $[43] = design.fontFamily;
      $[44] = design.shadow;
      $[45] = design.textPrimary;
      $[46] = design.textSecondary;
      $[47] = handleProjectLinkClick;
      $[48] = isDark;
      $[49] = t;
      $[50] = t15;
    } else {
      t15 = $[50];
    }
    t8 = <div className="grid gap-12">{filteredProjects.map(t15)}{filteredProjects.length === 0 && <div className={`p-12 text-center border-2 border-dashed ${design.borderRadius} ${design.border} ${design.textSecondary} opacity-50`}><p>{t("projects.emptyState")}</p></div>}</div>;
    t5 = "mt-16 text-center";
    T0 = Link;
    t2 = "/projects";
    t3 = `inline-flex items-center gap-2 text-base font-semibold ${design.accent} hover:opacity-80 transition-opacity`;
    t4 = t("projects.viewAllWithCount", {
      count: projData.length
    });
    $[3] = activeMode;
    $[4] = design.accent;
    $[5] = design.border;
    $[6] = design.borderRadius;
    $[7] = design.cardBg;
    $[8] = design.cardBorder;
    $[9] = design.fontFamily;
    $[10] = design.shadow;
    $[11] = design.textPrimary;
    $[12] = design.textSecondary;
    $[13] = handleProjectLinkClick;
    $[14] = isDark;
    $[15] = locale;
    $[16] = t;
    $[17] = T0;
    $[18] = t2;
    $[19] = t3;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
  } else {
    T0 = $[17];
    t2 = $[18];
    t3 = $[19];
    t4 = $[20];
    t5 = $[21];
    t6 = $[22];
    t7 = $[23];
    t8 = $[24];
  }
  let t9;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <ArrowRight className="h-5 w-5" />;
    $[51] = t9;
  } else {
    t9 = $[51];
  }
  let t10;
  if ($[52] !== T0 || $[53] !== t2 || $[54] !== t3 || $[55] !== t4) {
    t10 = <T0 href={t2} className={t3}>{t4}{t9}</T0>;
    $[52] = T0;
    $[53] = t2;
    $[54] = t3;
    $[55] = t4;
    $[56] = t10;
  } else {
    t10 = $[56];
  }
  let t11;
  if ($[57] !== t10 || $[58] !== t5) {
    t11 = <div className={t5}>{t10}</div>;
    $[57] = t10;
    $[58] = t5;
    $[59] = t11;
  } else {
    t11 = $[59];
  }
  let t12;
  if ($[60] !== t11 || $[61] !== t6 || $[62] !== t7 || $[63] !== t8) {
    t12 = <section data-section-id={t6}>{t7}{t8}{t11}</section>;
    $[60] = t11;
    $[61] = t6;
    $[62] = t7;
    $[63] = t8;
    $[64] = t12;
  } else {
    t12 = $[64];
  }
  return t12;
}